import { useEffect, useRef, useState } from 'react';
import type { StoryboardSegmentPreviewEntry } from '../types';
import { ensureMediaRecordForPreview, getMediaObjectUrl } from '../utils/mediaCache';

interface UseStoryboardMediaSourceOptions {
  projectId?: string | null;
  segmentId?: string | null;
  entry?: StoryboardSegmentPreviewEntry | null;
  autoFetch?: boolean;
}

interface UseStoryboardMediaSourceResult {
  src: string | null;
  isLocal: boolean;
  loading: boolean;
  error: Error | null;
}

export const useStoryboardMediaSource = (
  options: UseStoryboardMediaSourceOptions
): UseStoryboardMediaSourceResult => {
  const { projectId, segmentId, entry, autoFetch = false } = options;
  const [src, setSrc] = useState<string | null>(entry?.url ?? null);
  const [isLocal, setIsLocal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const resetToRemote = () => {
      const fallback = entry?.url ?? null;
      setSrc(fallback);
      setIsLocal(false);
    };

    const cleanupObjectUrl = () => {
      const current = objectUrlRef.current;
      if (current) {
        URL.revokeObjectURL(current);
        objectUrlRef.current = null;
      }
    };

    const run = async () => {
      setError(null);
      if (!entry?.previewId) {
        cleanupObjectUrl();
        setSrc(null);
        setIsLocal(false);
        setLoading(false);
        return;
      }
      if (!projectId || !segmentId || typeof window === 'undefined') {
        cleanupObjectUrl();
        resetToRemote();
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const local = await getMediaObjectUrl(entry.previewId);
        if (cancelled) {
          if (local?.objectUrl) {
            URL.revokeObjectURL(local.objectUrl);
          }
          return;
        }
        if (local) {
          cleanupObjectUrl();
          objectUrlRef.current = local.objectUrl;
          setSrc(local.objectUrl);
          setIsLocal(true);
          setLoading(false);
          return;
        }
        setIsLocal(false);
        if (autoFetch && (entry.url || entry.downloadSource)) {
          const record = await ensureMediaRecordForPreview({
            projectId,
            segmentId,
            entry,
          });
          if (cancelled) {
            return;
          }
          if (record) {
            const saved = await getMediaObjectUrl(record.previewId);
            if (cancelled) {
              if (saved?.objectUrl) {
                URL.revokeObjectURL(saved.objectUrl);
              }
              return;
            }
            if (saved) {
              cleanupObjectUrl();
              objectUrlRef.current = saved.objectUrl;
              setSrc(saved.objectUrl);
              setIsLocal(true);
              setLoading(false);
              return;
            }
          }
        }
        cleanupObjectUrl();
        resetToRemote();
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        console.error('[storyboard] 加载视频缓存失败', err);
        setError(err instanceof Error ? err : new Error('加载视频缓存失败'));
        cleanupObjectUrl();
        resetToRemote();
        setLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
      cleanupObjectUrl();
    };
  }, [projectId, segmentId, entry, entry?.previewId, entry?.url, entry?.downloadSource, autoFetch]);

  return { src, isLocal, loading, error };
};

export default useStoryboardMediaSource;
